import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loading from 'src/components/common/Loading';
import {
  v2SessionInfo,
  v2GetDivisions,
  v2SwitchDivision,
  type DivisionMembership,
} from 'src/store/modules/auth/v2Operations';
import {
  getAppUrlForReferrer,
  getReferrerInfo,
  resolveStageForApp,
  type AppStage,
} from 'src/utils/ssoReferrer';

const VALID_STAGES: ReadonlySet<AppStage> = new Set<AppStage>([
  'prod', 'dev', 'qa', 'localhost', 'beta', 'gamma',
]);

/**
 * Strip any known stage suffix from the `?app=` value so it compares
 * correctly against `appAccess` map keys (which are bare app names like
 * `shopperos`, `atlas`). Some upstream callers historically sent a full
 * referrer string (`shopperos-gamma`); the canonical form going forward
 * is the bare app name. Both shapes resolve to the same identifier here.
 */
const STAGE_SUFFIX_RE = /-(?:localhost|dev|qa|alpha|staging|beta|gamma)$/;
function stripStageSuffix(referrer: string): string {
  return referrer.replace(STAGE_SUFFIX_RE, '');
}

/**
 * Read the originating app's stage from the `?stage=` query param if present.
 *
 * The SSO host is shared between dev and prod (`signin.stackline.com`), so
 * `getReferrerInfo().stage` (which derives stage from the current hostname)
 * always returns `prod` here for both dev and prod traffic. Apps redirecting
 * to `/no-access` now forward their actual page stage via this param so we
 * can route the user back to the matching stage after picking an accessible
 * app. Prod traffic omits the param entirely (backward compat).
 *
 * Returns null when the param is missing, empty, or not a recognized stage.
 */
function readStageOverride(params: URLSearchParams): AppStage | null {
  const raw = params.get('stage')?.trim().toLowerCase();
  if (!raw) return null;
  return VALID_STAGES.has(raw as AppStage) ? (raw as AppStage) : null;
}

const APP_REDIRECT_PRIORITY = ['atlas', 'beacon', 'advisor', 'admanager'];

const CenteredContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '16px'
});

function divisionHasApp(division: DivisionMembership | undefined, app: string): boolean {
  const level = division?.appAccess?.[app];
  return !!level && level !== 'blocked';
}

function divisionHasAnyApp(division: DivisionMembership | undefined): boolean {
  if (!division?.appAccess) return false;
  return Object.values(division.appAccess).some(
    (level) => Boolean(level) && level !== 'blocked',
  );
}

const NoAccessRedirect: React.FC<RouteComponentProps> = ({ history }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolve = async () => {
      const params = new URLSearchParams(window.location.search);
      // Strip stage suffix so `deniedApp` is a bare app name comparable to
      // `appAccess` map keys. Upstream callers may send either `shopperos`
      // or the legacy `shopperos-gamma` form; both resolve to `shopperos`.
      const deniedApp = stripStageSuffix((params.get('app') || 'atlas').toLowerCase());
      const divisionName = params.get('divName') || 'your division';

      // Resolve the originating page's stage once. Apps redirecting to
      // /no-access forward `?stage=` so we can route the user back to the
      // matching environment (the SSO host is shared between dev and prod).
      // Prod traffic intentionally omits the param.
      const stageOverride = readStageOverride(params);
      const stage = stageOverride ?? getReferrerInfo().stage;

      const buildAppUrl = (app: string): string => {
        // Fall back to prod when the target app has no deployment for the
        // requested stage (e.g. atlas/beacon/advisor/admanager have no
        // `-gamma` / `-beta` hosts — only shopperos/omni do). Without this
        // a shopperos-gamma user bouncing to atlas would land on the
        // nonexistent `atlas-gamma.stackline.com`.
        const appStage = resolveStageForApp(app, stage);
        const referrer = appStage === 'prod' ? app : `${app}-${appStage}`;
        const baseUrl = getAppUrlForReferrer(referrer);
        const sep = baseUrl.includes('?') ? '&' : '?';
        const redirectParams = `sl_redirect=1&sl_from=${encodeURIComponent(deniedApp)}&sl_div=${encodeURIComponent(divisionName)}`;
        return `${baseUrl}${sep}${redirectParams}`;
      };

      try {
        const [sessionInfo, divisionsResp] = await Promise.all([
          v2SessionInfo(),
          v2GetDivisions().catch(() => null),
        ]);

        if (!sessionInfo.authenticated) {
          // Cookie is missing / expired / revoked. A bare push to /sign_in
          // would leave the stale cookie in the browser AND keep Cognito's
          // hosted-UI session alive -- SAML re-auth would then silently
          // re-issue a token for the same user without showing the IdP
          // prompt. Route through /sign_out so SignOut.tsx revokes the
          // server JTI, clears local cookies + Redux + division storage,
          // and redirects through Cognito's logout before landing the
          // user back at signin. Mirrors App_v2.jsx's 401 handler.
          history.push('/sign_out');
          return;
        }

        const divisions = divisionsResp?.divisions ?? [];
        const activeDivision = divisions.find(d => d.id === sessionInfo.divisionId);

        // Short-circuit: if no division anywhere grants any non-blocked
        // app, Priority 1/2 have nothing to pick — Priority 3 already
        // signs out, but doing it up-front avoids the case where an
        // upstream app sees one division's stray appAccess entry as
        // "accessible" and we bounce app-to-app before settling. Calling
        // apps (useDivisionCheck) now do the same pre-check, so this is
        // belt-and-suspenders for any other entry into /no-access.
        if (!divisions.some(divisionHasAnyApp)) {
          history.push('/sign_out');
          return;
        }

        // Priority 1: different app, SAME division. Prefer staying in the
        // user's current division context -- if they have any other
        // Stackline app accessible there, route to it. Less surprising
        // than a silent division switch, and matches the existing
        // "fall back to a different app" behavior customers expect.
        const accessibleAppsInCurrent = activeDivision?.appAccess
          ? Object.entries(activeDivision.appAccess)
              .filter(([name, level]) => name !== deniedApp && level && level !== 'blocked')
              .map(([name]) => name)
          : [];
        const fallbackApp =
          APP_REDIRECT_PRIORITY.find(a => accessibleAppsInCurrent.includes(a)) ||
          accessibleAppsInCurrent[0];
        if (fallbackApp) {
          window.location.href = buildAppUrl(fallbackApp);
          return;
        }

        // Priority 2: same app, DIFFERENT division. Current division has
        // nothing usable -- look for another division that has the
        // requested app and silently switch to it. The backend's per-app
        // last-active key gets written as a side effect of v2SwitchDivision
        // (referrer = deniedApp) so the next login via this app prefers
        // this division as the default. `divisions` comes back in
        // CreatedAt asc order, so the LAST capable entry is the newest.
        const sameAppOtherDivisions = divisions.filter(
          d => d.id !== sessionInfo.divisionId && divisionHasApp(d, deniedApp),
        );
        if (sameAppOtherDivisions.length > 0) {
          const target = sameAppOtherDivisions[sameAppOtherDivisions.length - 1];
          // Same stage-availability rule as `buildAppUrl` — the referrer
          // sent to `v2SwitchDivision` must match a real app/stage pair so
          // the API's per-app last-active-division key is keyed correctly
          // (and so `NormalizeAppName` resolves to the right app for the
          // ShopperOS-locked-division bypass).
          const switchStage = resolveStageForApp(deniedApp, stage);
          const referrer = switchStage === 'prod' ? deniedApp : `${deniedApp}-${switchStage}`;
          await v2SwitchDivision(target.id, referrer);
          window.location.href = buildAppUrl(deniedApp);
          return;
        }

        // Priority 3: terminal -- current division has no accessible app
        // and no other division has the requested app either. Sign the
        // user out so they restart from a clean session; the sign-in page
        // is the natural surface for "you have no usable access right
        // now" rather than a bespoke component here.
        history.push('/sign_out');
      } catch (err: any) {
        if (err.response?.status === 401) {
          // Same rationale as the !authenticated branch above -- route
          // through /sign_out for full session cleanup rather than just
          // pushing to /sign_in with a stale cookie still in the jar.
          history.push('/sign_out');
        } else {
          setError('Something went wrong. Please try signing in again.');
        }
      }
    };

    resolve();
  }, [history]);

  return (
    <CenteredContainer>
      <Loading />
      <Typography variant="body1" color="text.secondary">
        {error || 'Checking access...'}
      </Typography>
    </CenteredContainer>
  );
};

export default withRouter(NoAccessRedirect);
